"use client";

import {
  ExclamationCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

import { Consultation } from "@/app/_components/consultation-card";
import { Badge } from "@/components/ui/badge";
import { Error } from "@/app/_components/error";
import { Loading } from "../loading";

import { useNotice } from "@/queries/use-notice";

import type { NoticeInfoCriticity } from "@/types";
import type { NoticeItem } from "../notice.types";

const getCriticidadeBadgeColor = (criticidade: NoticeInfoCriticity): string => {
  switch (criticidade) {
    case "Alta":
      return "bg-red-500 hover:bg-unset";
    case "Média":
      return "bg-amber-500 hover:bg-unset";
    case "Baixa":
      return "bg-primary hover:bg-unset";
    default:
      return "bg-gray-500 hover:bg-unset";
  }
};

const getCriticidadeIcon = (criticidade: NoticeInfoCriticity) => {
  switch (criticidade) {
    case "Alta":
      return <XCircleIcon className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />;
    case "Média":
      return (
        <ExclamationCircleIcon className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
      );
    case "Baixa":
      return (
        <ExclamationCircleIcon className="h-5 w-5 text-primary mt-0.5 shrink-0" />
      );
    default:
      return (
        <ExclamationCircleIcon className="h-5 w-5 text-gray-500 mt-0.5 shrink-0" />
      );
  }
};

export const Riscos = ({ id, field, emptyElement }: NoticeItem) => {
  const { data, isPending, error, refetch } = useNotice({ id, field });

  if (isPending) return <Loading />;

  if (error) return <Error reset={refetch} />;

  const notice = data?.analise.data;

  if (!notice) {
    return (
      emptyElement ?? (
        <p className="text-center text-base text-muted-foreground w-4/6">
          A análise do edital provavelmente não está disponível. Se você tiver
          alguma dúvida, não hesite em entrar em contato conosco.
        </p>
      )
    );
  }

  return (
    <Consultation.Card
      title="Riscos e Pontos Críticos"
      description="Potenciais problemas identificados pela análise"
    >
      <ul className="flex flex-col gap-6">
        {notice.riscos_e_pontos_criticos.map((risco, index) => (
          <li
            key={index}
            className="flex flex-col gap-2 border-b pb-3 last:border-0 last:pb-0"
          >
            <div className="flex items-center justify-between">
              <h2 className="font-medium flex items-center gap-2">
                {getCriticidadeIcon(risco.criticidade)}
                <span>{risco.titulo}</span>
              </h2>
              <Badge className={getCriticidadeBadgeColor(risco.criticidade)}>
                {risco.criticidade}
              </Badge>
            </div>
            <p className="text-muted-foreground pl-7">{risco.descricao}</p>
          </li>
        ))}
      </ul>
    </Consultation.Card>
  );
};
